import {
  ActivityStatus,
  QActivitySequence,
  QStack,
  QText,
} from '@qualio/ui-components';
import { Parser } from 'html-to-react';
import { useTranslation } from 'react-i18next';
import { useFormatDate } from '../../../../hooks/useFormatDate';

interface CommentThreadProps {
  thread: CommentsRepository.CommentThreadFetched;
}

type SuggestionStatus = 'accepted' | 'declined';

export const AcceptedSuggestionThread = ({ thread }: CommentThreadProps) => {
  const { t } = useTranslation();
  const formatDate = useFormatDate();
  const activities = parseSuggestionInThread(thread, 'accepted', formatDate, t);
  if (activities) {
    return (
      <QActivitySequence
        activities={activities}
        key={thread.id}
        data-cy="comment-history-accept-suggestion-element"
      />
    );
  } else {
    return null;
  }
};

export const DeclinedSuggestionThread = ({ thread }: CommentThreadProps) => {
  const { t } = useTranslation();
  const formatDate = useFormatDate();
  const activities = parseSuggestionInThread(thread, 'declined', formatDate, t);
  if (activities) {
    return (
      <QActivitySequence
        activities={activities}
        key={thread.id}
        data-cy="comment-history-declined-suggestion-element"
      />
    );
  } else {
    return null;
  }
};

const parseSuggestionInThread = (
  thread: CommentsRepository.CommentThreadFetched,
  status: SuggestionStatus,
  formatDate: (utcDate: Date | string, includeTime?: boolean) => string,
  t: (key: string) => string,
) => {
  const htmlToReactParser = new Parser();
  const suggestionAction = getSuggestionAction(thread.suggestion_content, t);
  if (!suggestionAction) {
    return undefined;
  }
  const activities = [
    {
      status: 'commented' as ActivityStatus,
      actionLabel: t('comments.suggested'),
      fullName: thread.author_full_name ?? t('comments.unknownUser'),
      actionTimestamp: thread.suggestion_created_at
        ? formatDate(thread.suggestion_created_at, true)
        : t('comments.unknownTime'),
      children: getSuggestionAction(thread.suggestion_content, t),
    },
    ...thread.comments.map((comment, index) => ({
      status: 'commented' as ActivityStatus,
      fullName: comment.attributes.authorFullName ?? comment.user.id,
      actionTimestamp: comment.created_at
        ? formatDate(comment.created_at, true)
        : t('comments.unknownTime'),
      children: (
        <QStack>
          <QText>{htmlToReactParser.parse(comment.content)}</QText>
        </QStack>
      ),
    })),
    {
      status: (status === 'accepted'
        ? 'approved'
        : 'declined') as ActivityStatus,
      actionLabel:
        status === 'accepted'
          ? t('comments.acceptedSuggestion')
          : t('comments.discardedSuggestion'),
      fullName: thread.last_updated_by ?? t('comments.unknownUser'),
      actionTimestamp: thread.suggestion_deleted_at
        ? formatDate(thread.suggestion_deleted_at, true)
        : t('comments.unknownTime'),
    },
  ];
  return activities;
};

const getSuggestionAction = (
  suggestionDataString: string | undefined,
  t: (key: string) => string,
) => {
  if (!suggestionDataString) {
    return undefined;
  }
  const match = suggestionDataString.match(/^\*([^*]*)\* ?(.*)$/);
  let action = '';
  let value = t('comments.notProvided');
  if (match) {
    action = match[1];
    value = match[2];
  }
  return (
    <QText>
      <b>{action}</b> {value}
    </QText>
  );
};
